import { isObject } from '@tunein/web-utils';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import reduce from 'lodash/reduce';
import remove from 'lodash/remove';
import queryString from 'qs';
import ApiError from '../errors/ApiError';
import { handleOpmlError } from '../errors/OpmlError';
import transformOpmlResponseHead from '../transforms/transformOpmlResponseHead';
import tuneInRequest from '../tuneInRequest';
export default function authService(config, apiContext, applyReauth) {
    const { opml = '', local = '', coreApi = '' } = config.domains;
    const accountEndpoint = `${opml}/Account.ashx`;
    const proxyEndpoint = `${local}/auth`;
    const loginResponseOmittedProps = ['element', 'text', 'guideId', 'key'];
    const partnerReauthEndpoint = `${coreApi}/account/refresh`;
    const coreApiHostname = coreApi.substring(8);
    function loginTransformPredicate(item) {
        return item.key === 'oauth';
    }
    function transformOpmlResponseBody({ body, ...otherData }) {
        const transformedBody = reduce(body, (result, value) => ({
            ...result,
            ...value,
        }));
        return {
            body: omit(transformedBody, loginResponseOmittedProps),
            ...otherData,
        };
    }
    function transformOpmlResponse(data) {
        if (!isObject(data)) {
            return data;
        }
        const tokenData = omit(first(remove(data.body, loginTransformPredicate)), loginResponseOmittedProps);
        const { head } = transformOpmlResponseHead(data);
        const { body } = transformOpmlResponseBody(data);
        return {
            head,
            ...(isEmpty(tokenData) ? { body } : { tokenData, userData: body }),
        };
    }
    function handleNoAuthData(response) {
        if (isEmpty(response.data.tokenData) || isEmpty(response.data.userData)) {
            throw new ApiError({
                message: 'Token/user data should not be empty',
                response,
                doNotIncludeStatus: true,
            });
        }
        return response;
    }
    function handleAccountAuthPromise(apiPromise) {
        return apiPromise
            .then(handleOpmlError)
            .then(handleNoAuthData)
            .then((response) => {
            apiContext.setTokens(response.data.tokenData, true);
            return response;
        });
    }
    function handleLogoutPromise(apiPromise) {
        return apiPromise.then(handleOpmlError).then((response) => {
            apiContext.clearTokens();
            return response;
        });
    }
    function proxyTuneInCommandRequest(endpoint, details) {
        return tuneInRequest({
            method: 'post',
            url: `${proxyEndpoint}/${endpoint}/`,
            isAjax: true,
            data: details,
            disableRetry: true,
        }, apiContext);
    }
    function proxyEmailLogin(details) {
        const accountApiPromise = proxyTuneInCommandRequest('emailLogin', details);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function proxyEmailVerification(details) {
        const accountApiPromise = proxyTuneInCommandRequest('emailVerification', details);
        return accountApiPromise.then(handleOpmlError);
    }
    function tuneInEmailCommandRequest(command, generateAuth, details) {
        const { username, password, reCAPTCHAResponse, params } = details;
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                ...params,
                c: command,
            },
            data: queryString.stringify({
                generateAuth,
                username,
                password,
                'g-recaptcha-response': reCAPTCHAResponse,
            }),
            transformResponse: transformOpmlResponse,
            disableRetry: true,
        }, apiContext);
    }
    function emailVerification(details) {
        const accountApiPromise = tuneInEmailCommandRequest('verify', false, details);
        return accountApiPromise.then(handleOpmlError);
    }
    function emailLogin(details) {
        const accountApiPromise = tuneInEmailCommandRequest('login', true, details);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function proxyTuneInOauthCommandRequest(endpoint, providerName, data, params) {
        const { userId, token, firstName, lastName } = data;
        return tuneInRequest({
            method: 'post',
            url: `${proxyEndpoint}/${endpoint}/`,
            isAjax: true,
            data: {
                providerName,
                userId,
                token,
                params,
                firstName,
                lastName,
            },
        }, apiContext);
    }
    function proxyOauthVerification(providerName, data, params) {
        const accountApiPromise = proxyTuneInOauthCommandRequest('oauthVerification', providerName, data, params);
        return accountApiPromise.then(handleOpmlError);
    }
    function proxyOauthLogin(providerName, data, params) {
        const accountApiPromise = proxyTuneInOauthCommandRequest('oauthLogin', providerName, data, params);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function tuneInOauthCommandRequest(command, generateAuth, details) {
        const { providerName, userId, token, params, firstName, lastName } = details;
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                ...params,
                c: command,
            },
            data: queryString.stringify({
                generateAuth,
                providerName,
                linkedProviderName: providerName,
                externalId: userId,
                linkedExternalId: userId,
                providerToken: token,
                linkedProviderToken: token,
                firstName,
                lastName,
            }),
            transformResponse: transformOpmlResponse,
        }, apiContext);
    }
    function oauthVerification(details) {
        const accountApiPromise = tuneInOauthCommandRequest('verify', false, details);
        return accountApiPromise.then(handleOpmlError);
    }
    function oauthLogin(details) {
        const accountApiPromise = tuneInOauthCommandRequest('login', true, details);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function createAccount(creds) {
        const accountApiPromise = tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                ...creds.params,
                c: 'create',
            },
            data: queryString.stringify({
                generateAuth: true,
                ...omit(creds, 'params'),
            }),
            transformResponse: transformOpmlResponse,
            disableRetry: true,
        }, apiContext);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function proxyCreateAccount(data) {
        const accountApiPromise = tuneInRequest({
            method: 'post',
            url: `${proxyEndpoint}/createAccount/`,
            isAjax: true,
            data,
            disableRetry: true,
        }, apiContext);
        return handleAccountAuthPromise(accountApiPromise);
    }
    function premiumUnlock(params) {
        const accountApiPromise = tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            params: {
                c: 'unlockContent',
                campaignId: params.campaignId,
                unlockProviderData: params.unlockProviderData,
                unlockToken: params.unlockToken,
            },
            disableCamelCase: true,
        }, apiContext);
        return accountApiPromise.then(handleOpmlError);
    }
    function addDevice(params) {
        const accountApiPromise = tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            params: {
                c: 'join',
                partnerId: params.partnerId,
                serial: params.serial,
            },
            disableCamelCase: true,
        }, apiContext);
        return accountApiPromise.then(handleOpmlError);
    }
    function proxyAddDevice(data) {
        const accountApiPromise = tuneInRequest({
            method: 'post',
            url: `${proxyEndpoint}/addDevice/`,
            isAjax: true,
            data,
            disableRetry: true,
        }, apiContext);
        return accountApiPromise.then(handleOpmlError);
    }
    function logout() {
        const apiPromise = tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            params: {
                c: 'drop',
            },
            transformResponse: transformOpmlResponse,
            disableRetry: true,
        }, apiContext);
        return handleLogoutPromise(apiPromise);
    }
    function proxyLogout() {
        const apiPromise = tuneInRequest({
            method: 'post',
            url: `${proxyEndpoint}/logout/`,
            isAjax: true,
            disableRetry: true,
        }, apiContext);
        return handleLogoutPromise(apiPromise);
    }
    function forgotPassword(email) {
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                c: 'reset',
            },
            data: queryString.stringify({
                email,
            }),
            transformResponse: transformOpmlResponse,
        }, apiContext).then(handleOpmlError);
    }
    function createChangeRequest(command, params) {
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                c: command,
            },
            data: queryString.stringify({
                ...params,
            }),
            transformResponse: transformOpmlResponse,
        }, apiContext).then(handleOpmlError);
    }
    function resetPassword(userToken, newPassword) {
        return createChangeRequest('changePassword', { userToken, newPassword });
    }
    function changePassword(newPassword) {
        return createChangeRequest('changePassword', { newPassword });
    }
    function changeUsername(newUsername) {
        return createChangeRequest('changeUsername', { newUsername });
    }
    function partnerReauth(data, apiKey) {
        return tuneInRequest({
            method: 'post',
            url: partnerReauthEndpoint,
            data,
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': apiKey,
                host: coreApiHostname,
            },
            disableRetry: true,
            withoutParams: true,
        }, apiContext).then(handleOpmlError);
    }
    function proxyDesktopGoogleAuthLogin(data) {
        const accountApiPromise = proxyTuneInCommandRequest('desktopGoogleAuth', data);
        return accountApiPromise.then((response) => {
            apiContext.setTokens(response.data.tokenData, true);
            return response.data;
        });
    }
    function beginDeviceGrantSession(params) {
        const { partnerId, serial, version, sku } = params || {};
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                c: 'beginDeviceGrantSession',
                partnerId,
                serial,
                version,
                sku,
            },
            transformResponse: transformOpmlResponse,
        }, apiContext).then(handleOpmlError);
    }
    function acceptDeviceGrant(params) {
        const { partnerId, serial, version, deviceCode } = params || {};
        return tuneInRequest({
            method: 'post',
            url: accountEndpoint,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            params: {
                c: 'acceptDeviceGrant',
                generateAuth: true,
                partnerId,
                serial,
                version,
                deviceCode,
            },
            transformResponse: transformOpmlResponse,
        }, apiContext).then(handleOpmlError);
    }
    return {
        emailVerification,
        emailLogin,
        logout: applyReauth(logout),
        oauthLogin,
        oauthVerification,
        createAccount,
        premiumUnlock: applyReauth(premiumUnlock),
        addDevice: applyReauth(addDevice),
        proxyAddDevice,
        proxyEmailVerification,
        proxyEmailLogin,
        proxyOauthLogin,
        proxyOauthVerification,
        proxyDesktopGoogleAuthLogin,
        proxyCreateAccount,
        proxyLogout,
        forgotPassword,
        resetPassword,
        partnerReauth,
        changePassword: applyReauth(changePassword),
        changeUsername: applyReauth(changeUsername),
        beginDeviceGrantSession,
        acceptDeviceGrant,
    };
}
